import * as React from 'react';
import { BaseModal, Button, Checkbox, Input, Select } from 'src/components';
import useModal from 'src/hooks/useModal';
import { useAlerts } from 'src/common/AlertManager';
import {
  useCancelMigrationCopyMutation,
  useStartMigrationCopyMutation,
} from 'src/api/mutations/migrationMutations';
import { useMigrationCopyQuery } from 'src/api/queries/migrationQueries';
import { MigrationOverview, MigrationRun, MigrationRunOptions } from 'src/types/TMigration';
import RunReport, { isTerminal } from './RunReport';
import { ExampleFileLink, ModalHeader, Note, Row, errorText, timeAgo } from './parts';
import styles from './styles.module.scss';

/** Wide enough for a six-column stat row, and scrollable rather than taller
 *  than the viewport. */
const MODAL_SIZE = { width: 'min(56rem, 92vw)', maxHeight: '88vh', overflowY: 'auto' } as const;

/**
 * "Copy everything now" — bringing the whole library across up front.
 *
 * A dialog, matching every other one-off action in the dashboard. Two facts
 * drive everything it says about itself:
 *
 *  - **Running it again is free.** Every image is checked against what we
 *    already hold before anything is fetched, so an interrupted copy is
 *    recovered by pressing the button again rather than by working out where it
 *    stopped.
 *  - **"Copied" has to mean the image works.** Creating the asset and being
 *    able to serve it are different events, so the copy waits for processing
 *    and reports what became of each one. The "cannot be used" number is never
 *    hidden or folded into another figure — it is the only place a customer
 *    learns some of their pictures do not work.
 *
 * Closing the dialog does NOT stop a copy in flight; the walk runs server-side
 * and its progress carries on showing in the section behind. Only the Stop
 * button stops it.
 */

type Props = {
  overview: MigrationOverview;
};

type Scope = 'everything' | 'prefix' | 'tag' | 'list';

/**
 * The example file offered beside the upload control.
 *
 * Deliberately shows all three shapes the reader might have — a bare path, a
 * path with a folder, and a full delivery URL — because the reader's real
 * question is "will my export work", and the honest answer is "any of these
 * do". Comment lines are supported and used to say so inside the file itself.
 */
const EXAMPLE_PATHS = `# One image per line. Any of these forms works.
# Lines starting with # are ignored.

products/shoe
products/summer/hat
banners/hero
https://res.cloudinary.com/your-cloud-name/image/upload/products/bag.jpg
`;

/** The CSV form. The header row names the column we read; `path`, `public_id`,
 *  `filepath`, `url`, `file`, `image`, `asset`, `key` and `name` are all
 *  accepted, so most exports work untouched. Extra columns are ignored, which
 *  is what makes a PIM or CMS export usable as-is. */
const EXAMPLE_PATHS_CSV = `path,notes
products/shoe,any other columns are ignored
products/summer/hat,
banners/hero,
https://res.cloudinary.com/your-cloud-name/image/upload/products/bag.jpg,a full URL works too
`;

const SCOPES = [
  { value: 'everything', label: 'Everything' },
  { value: 'prefix', label: 'One folder' },
  { value: 'tag', label: 'Whatever I tagged at my provider' },
  { value: 'list', label: 'A list of paths I have' },
];

export const CopyLibraryModal: React.FC<Props> = ({ overview }) => {
  const { closeModal } = useModal();
  const { showSuccess, showAlert } = useAlerts();
  const start = useStartMigrationCopyMutation();
  const cancel = useCancelMigrationCopyMutation();

  const [scope, setScope] = React.useState<Scope>('everything');
  const [prefix, setPrefix] = React.useState('');
  const [tag, setTag] = React.useState('');
  const [paths, setPaths] = React.useState('');
  const [pathsSource, setPathsSource] = React.useState('');
  const [exclude, setExclude] = React.useState('');
  const [limit, setLimit] = React.useState('');
  const [retryBroken, setRetryBroken] = React.useState(false);
  const [showMore, setShowMore] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  // Watch whatever is running, or the most recent copy, so reopening the dialog
  // after one finished still shows its report.
  const watchedId = overview.active_run?.id ?? overview.runs[0]?.id;
  const seed = overview.active_run ?? overview.runs[0];
  const { data: polled } = useMigrationCopyQuery(watchedId, {
    initialData: seed,
    // Poll only while something is moving. A finished copy polled every three
    // seconds forever is a request per tab, indefinitely.
    refetchInterval: (data) => (isTerminal(data as MigrationRun) ? false : 3000),
  });
  const run = polled ?? seed;
  /**
   * "Busy" has to include the request that starts a copy, not just a copy the
   * server has confirmed.
   *
   * `running` comes from polled data, so between the click and the first poll
   * the buttons stayed live and a second click started a second walk — which is
   * how a run ends up reported as interrupted. `justStarted` closes that window
   * and only clears once the poll reports something in flight.
   */
  const [justStarted, setJustStarted] = React.useState(false);
  const running = !isTerminal(run);
  React.useEffect(() => {
    if (running) setJustStarted(false);
  }, [running]);
  const busy = running || justStarted || start.isLoading;

  const canList = Boolean(overview.origin?.can_list);
  const pathList = paths
    .split('\n')
    .map((p) => p.trim())
    .filter((p) => p && !p.startsWith('#'));

  // A copy needs either the vendor's listing (an API key) or a list of paths.
  // With neither, every button here would fail server-side with a 401 the
  // customer has to go and decode.
  const blocked = !canList && (scope !== 'list' || pathList.length === 0);
  const blockedReason = canList
    ? undefined
    : 'Add a working API key under “Your old provider”, or choose “A list of paths I have” and paste them.';

  const options = (dryRun: boolean): MigrationRunOptions => ({
    dry_run: dryRun,
    limit: parseInt(limit, 10) || 0,
    prefix: scope === 'prefix' ? prefix.trim() : '',
    tag: scope === 'tag' ? tag.trim() : '',
    exclude: exclude
      .split('\n')
      .map((g) => g.trim())
      .filter(Boolean),
    retry_broken: retryBroken,
    paths: scope === 'list' ? pathList : [],
    paths_source: scope === 'list' ? pathsSource : undefined,
  });

  const launch = async (dryRun: boolean) => {
    if (scope === 'prefix' && !prefix.trim()) return showAlert('Enter the folder to copy');
    if (scope === 'tag' && !tag.trim()) return showAlert('Enter the tag to copy');
    if (scope === 'list' && pathList.length === 0) return showAlert('Paste or upload the paths to copy');
    setJustStarted(true);
    try {
      await start.mutateAsync(options(dryRun));
      showSuccess(dryRun ? 'Preview started' : 'Copying started');
    } catch (err) {
      setJustStarted(false);
      showAlert(errorText(err, 'Could not start'));
    }
  };

  const stop = async () => {
    if (!run) return;
    try {
      await cancel.mutateAsync(run.id);
      showSuccess('Stopping — everything copied so far stays copied');
    } catch (err) {
      showAlert(errorText(err, 'Could not stop'));
    }
  };

  const onFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setPaths(await file.text());
      setPathsSource(file.name);
      setScope('list');
    } catch {
      showAlert('Could not read that file');
    } finally {
      event.target.value = '';
    }
  };

  return (
    // BaseModal puts `className` on BOTH the full-screen backdrop and the
    // dialog inside it, so sizing through it shrinks the overlay and the
    // dialog stops being centred. `containerStyle` reaches only the dialog.
    <BaseModal containerStyle={MODAL_SIZE}>
      <ModalHeader
        title="Copy your library"
        lead="Bring your images across now instead of waiting for visitors to ask for them. You can stop
              and start again whenever you like — a second run only copies what the first one missed."
      />

      {/* Without a way to read the library, the only usable option is a list
          the customer supplies. Saying so up front — and disabling the buttons
          until one of the two is true — beats letting them press Start and read
          a 401 out of a run report. */}
      {!canList && (
        <Note tone="warn">
          <strong>We cannot read a list of your images yet.</strong> Add a working API key under “Your
          old provider” to copy everything in one go, or paste the paths you want below.
        </Note>
      )}

      <Row label="What to copy">
        <Select value={scope} onChange={(v) => setScope(v as Scope)} options={SCOPES} />
      </Row>

      {scope === 'prefix' && (
        <Row
          label="Folder"
          stacked
          help="We ask your provider for just this folder, so it costs one small request rather than reading your whole library."
        >
          <Input value={prefix} onChange={setPrefix} placeholder="products/" />
        </Row>
      )}

      {scope === 'tag' && (
        <Row
          label="Tag"
          stacked
          help="The easiest option: tag what you want in your provider's own dashboard and we read it. No list has to pass between us."
        >
          <Input value={tag} onChange={setTag} placeholder="migrate" />
        </Row>
      )}

      {scope === 'list' && (
        <Row
          label="Paths"
          stacked
          help="One per line. Full image URLs work too. From a spreadsheet, export as CSV — we read the column named path, public_id, url, file or name, and ignore the rest."
        >
          <textarea
            className={styles.textarea}
            value={paths}
            onChange={(e) => {
              setPaths(e.target.value);
              setPathsSource('');
            }}
            rows={6}
            placeholder={'products/shoe\nproducts/hat\nbanners/hero'}
          />
          <div className={styles.sectionActions}>
            {/* A real button. As `mode="flat"` this rendered as plain text and
                nobody could tell it was clickable. */}
            <Button mode="secondary" outlined onClick={() => fileRef.current?.click()}>
              Upload a .txt or .csv
            </Button>
            <ExampleFileLink
              filename="example-image-list.txt"
              contents={EXAMPLE_PATHS}
              label="Example .txt"
            />
            <ExampleFileLink
              filename="example-image-list.csv"
              contents={EXAMPLE_PATHS_CSV}
              label="Example .csv"
            />
            {pathList.length > 0 && <span className={styles.rowHelp}>{pathList.length} paths</span>}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.csv,text/plain,text/csv"
            className={styles.hiddenFile}
            onChange={onFile}
          />
        </Row>
      )}

      <button type="button" className={styles.disclosure} onClick={() => setShowMore((v) => !v)}>
        {showMore ? '▾' : '▸'} More options
      </button>

      {showMore && (
        <>
          <Row label="Stop after" help="Leave empty to copy everything that matches.">
            <Input value={limit} onChange={setLimit} type="number" min={0} placeholder="e.g. 500" />
          </Row>
          <Row
            label="Leave some folders out"
            stacked
            /* This said: "One pattern per line. * stops at a slash, ** does
               not." Both statements are true and neither told a customer what
               to type — the reviewer's word for it was that they did not
               understand what the field was for. So the rule is shown by
               example instead of stated, because the difference between one
               star and two is only ever clear next to a folder name. */
            help={
              <>
                One per line. Anything that matches is skipped.
                <span className={styles.helpExamples}>
                  <span>
                    <code>archive/**</code> — everything inside <em>archive</em>, subfolders included
                  </span>
                  <span>
                    <code>drafts/*</code> — the files directly inside <em>drafts</em>, but not its subfolders
                  </span>
                </span>
              </>
            }
          >
            <textarea
              className={styles.textarea}
              value={exclude}
              onChange={(e) => setExclude(e.target.value)}
              rows={2}
              placeholder={'archive/**\n*/tmp-*'}
            />
          </Row>
          <Row
            compact
            label="Retry images that failed before"
            help="Off by default — an image we could not process fails the same way every time. Turn this on once the cause is fixed."
          >
            <Checkbox checked={retryBroken} onChange={() => setRetryBroken((v) => !v)} />
          </Row>
        </>
      )}

      <div className={styles.modalActions}>
        <Button
          onClick={() => launch(false)}
          loading={start.isLoading}
          disabled={busy || blocked}
          tooltip={blocked ? blockedReason : undefined}
        >
          Start copying
        </Button>
        <Button
          mode="secondary"
          outlined
          onClick={() => launch(true)}
          loading={start.isLoading}
          disabled={busy || blocked}
          tooltip={blocked ? blockedReason : undefined}
        >
          Preview first
        </Button>
        {running && run?.cancellable && (
          <Button mode="decline" outlined onClick={stop} loading={cancel.isLoading}>
            Stop
          </Button>
        )}
        <Button mode="flat" onClick={closeModal}>
          Close
        </Button>
      </div>
      <p className={styles.rowHelp}>
        “Start copying” fetches your images from your provider and stores them here for real. “Preview
        first” only counts what would be copied and creates nothing. Closing this window does not stop a
        copy that is already running.
      </p>

      {run && <RunReport run={run} />}

      {overview.runs.length > 1 && (
        <table className={styles.table}>
          <tbody>
            {overview.runs.slice(1, 6).map((r) => (
              <tr key={r.id}>
                <td>
                  {r.dry_run ? 'Preview' : 'Copy'} · {timeAgo(r.created_at)}
                </td>
                <td className={styles.tableNum}>
                  {r.stats
                    ? `${r.stats.copied} ${r.dry_run ? 'would copy' : 'copied'} · ${r.stats.skipped} already here`
                    : r.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </BaseModal>
  );
};

export default CopyLibraryModal;
